import React, { useEffect } from 'react';
import { CodeBlock, atomOneLight } from 'react-code-blocks';
import { getBrandName } from '../utils/brand';
import Callout from './Callout';

const WebhooksReference = () => {
    const brandName = getBrandName();
    const brandSlug = brandName.replace(/\s+/g, '');
    const brandSignatureHeader = `X-${brandSlug}-Signature`;
    const isGreenline = typeof window !== 'undefined' && window.location.host.toLowerCase().includes('greenline.works');
    const knowledgeBaseUrl = isGreenline
        ? 'https://greenline.works/knowledge-base/webhooks/'
        : 'https://kanbanzone.com/knowledge-base/webhooks/';

    useEffect(() => {
        document.title = `Webhooks · ${brandName} Docs`;
    }, [brandName]);

    const notificationPayloadSample = `{
    "notification": {
        "type": "CARD_CREATED",
        "hookDateTime": "2026-04-30T18:18:09.750Z",
        "payload": {
            "CardItem": { /* see CardItemOutputModel in API Reference */ }
        }
    }
}`;

    const javascriptDecodeExample = `// Verify a ${brandName} webhook payload signature
const crypto = require('crypto');

// Your ${brandName} Webhook Key (from Settings → Integrations → Webhook Key)
const secretKey = 'KeyGeneratedFrom${brandSlug}';

// HMAC-sha1 the payload using your key
const myHash = crypto.createHmac('sha1', secretKey)
    .update(JSON.stringify(req.body.payload))
    .digest('hex');

// Compare to the signature from the request header
const webhookSignature = req.headers['${brandSignatureHeader.toLowerCase()}'];

if (webhookSignature === myHash) {
    // ✓ Verified — request is from ${brandName}
} else {
    // ✗ Signature mismatch — reject the request
}`;

    return (
        <div className="swagger-ui doc-page">
            <div className="information-container wrapper">
                <div className="info">
                    <h2 className="title">Webhooks</h2>

                    <h2>Overview</h2>
                    <p>
                        Webhooks notify your application when an event happens in your {brandName} organization.
                        When a subscribed event fires, {brandName} sends an <b>HTTPS POST</b> request to the destination
                        URL you registered with the event payload.
                    </p>
                    <p>
                        See our{' '}
                        <a className="link" rel="noopener noreferrer" target="_blank" href={knowledgeBaseUrl}>
                            Knowledge Base
                        </a>{' '}
                        for the full setup walkthrough.
                    </p>

                    <h2>Supported events</h2>
                    <p>The following card events are available:</p>
                    <ul className="circle">
                        <li><code>CARD_CREATED</code> — fires when a new card is created on a board.</li>
                        <li><code>CARD_MOVED</code> — fires when a card moves between columns.</li>
                        <li><code>CARD_UPDATED</code> — fires when an existing card is edited.</li>
                    </ul>
                    <p>
                        The payload <b>CardItem</b> format is documented as <code>CardItemOutputModel</code> in the{' '}
                        <a className="link" rel="noopener noreferrer" href="/api">API Reference</a>.
                    </p>

                    <h2>Payload shape</h2>
                    <p>Every webhook delivery has the following envelope:</p>
                    <CodeBlock
                        text={notificationPayloadSample}
                        language="json"
                        showLineNumbers
                        wrapLines
                        theme={atomOneLight}
                        codeBlock
                    />
                    <p>
                        <code>notification.type</code> identifies the event; <code>notification.hookDateTime</code> is
                        the ISO timestamp of the event.
                    </p>

                    <h2>Security: verifying signatures</h2>
                    <Callout type="security" title="Always verify the signature">
                        Generate a Webhook Key in {brandName} and {brandName} will sign every payload with HMAC-SHA1.
                        The signature arrives in the <code>{brandSignatureHeader}</code> request header. Re-compute the
                        hash on your side and compare — only handle the request if they match.
                    </Callout>
                    <CodeBlock
                        text={javascriptDecodeExample}
                        language="javascript"
                        showLineNumbers
                        wrapLines
                        theme={atomOneLight}
                        codeBlock
                    />
                </div>
            </div>
        </div>
    );
};

export default WebhooksReference;
